import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Send, ImagePlus, X, Check, Users, Upload, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContributorPortal = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("intro"); // intro | form | uploading | done
  const [name, setName] = useState("");
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API}/occasions/contribute/${slug}`);
        setEvent(res.data);
        const initial = {};
        res.data.prompts.forEach((p, i) => { initial[i] = ""; });
        setAnswers(initial);
      } catch (err) {
        setError(err.response?.status === 400 ? "This event is no longer accepting contributions." : "Event not found.");
      }
      setLoading(false);
    };
    fetchEvent();
  }, [slug]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/occasions/contribute/${slug}/upload`, formData);
        setPhotos((prev) => [...prev, res.data.photo_url]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    const hasAnswer = Object.values(answers).some((a) => a.trim().length > 0);
    if (!hasAnswer) {
      toast.error("Please answer at least one question");
      return;
    }

    setSubmitting(true);
    try {
      const responses = event.prompts.map((prompt, i) => ({
        prompt,
        answer: answers[i] || ""
      })).filter((r) => r.answer.trim().length > 0);

      await axios.post(`${API}/occasions/contribute/${slug}`, {
        contributor_name: name.trim(),
        responses,
        photo_urls: photos
      });
      setStep("done");
      toast.success("Thank you for your contribution!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center p-6">
        <div className="text-center max-w-md" data-testid="contributor-error">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#E5D4C1]/40 flex items-center justify-center">
            <Users className="w-8 h-8 text-[#8A7A6A]" />
          </div>
          <h2 className="font-serif text-2xl text-[#4A5D4A] mb-3">{error || "Event not found"}</h2>
          <Link to="/" className="font-sans text-sm text-[#7A9B7E] hover:underline">Go to homepage</Link>
        </div>
      </div>
    );
  }

  // Done screen
  if (step === "done") {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center p-6" data-testid="contribution-success">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D1FAE5] flex items-center justify-center">
            <Check className="w-10 h-10 text-[#059669]" />
          </div>
          <h2 className="font-serif text-3xl text-[#2D3A2D] mb-3">Thank you, {name}!</h2>
          <p className="font-sans text-base text-[#7A8A7A] mb-6">
            Your memories for {event.subject_name} have been saved. They'll be woven into a beautiful collaborative story.
          </p>
          <p className="font-sans text-sm text-[#9FB89F] mb-6">{event.contribution_count + 1} people have contributed so far</p>

          {/* Viral Loop Banner */}
          <div className="bg-[#7A9B7E]/10 border border-[#7A9B7E]/30 rounded-xl p-5 mb-6" data-testid="viral-loop-banner">
            <p className="font-sans text-sm font-medium text-[#2D3A2D] mb-1">Your memory has been added!</p>
            <p className="font-sans text-xs text-[#7A8A7A]">Create your own account to start building your own books.</p>
          </div>

          <Link to="/login" className="inline-flex items-center gap-2 px-8 py-3 bg-[#4A5D4A] text-white rounded-full font-sans font-medium text-sm hover:bg-[#2D3A2D] transition-all shadow-md" data-testid="viral-cta">
            <Leaf className="w-4 h-4" /> Create Your Own Story
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6F1] relative" data-testid="contributor-portal">
      {/* Decorative shapes */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9FB89F]/10 organic-blob" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-[#E5D4C1]/15 organic-blob" style={{ animationDelay: "2s" }} />

      {/* Header */}
      <div className="relative z-10 bg-gradient-to-br from-[#9FB89F] to-[#5A7B5E] text-white">
        <div className="max-w-2xl mx-auto px-6 py-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Leaf className="w-5 h-5 text-white/70" />
            <span className="font-sans text-xs text-white/70 uppercase tracking-wider">My Story — Collaborative</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium mb-3" data-testid="event-title">{event.title}</h1>
          <p className="font-sans text-white/80 text-base">
            {event.host_name} invited you to share your memories of <strong>{event.subject_name}</strong>
          </p>
          <p className="font-sans text-white/60 text-sm mt-2">{event.contribution_count} {event.contribution_count === 1 ? "person has" : "people have"} contributed</p>
        </div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-10">
        {/* Intro step */}
        {step === "intro" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-3xl p-8 shadow-lg">
            <h2 className="font-serif text-2xl text-[#2D3A2D] mb-2">Share your memories</h2>
            <p className="font-sans text-sm text-[#7A8A7A] mb-6">
              Answer a few questions about {event.subject_name}. Your words will be woven into a beautiful collaborative story.
            </p>
            <div>
              <label className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-2 block">Your name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]"
                data-testid="contributor-name-input"
              />
            </div>
            <button
              onClick={() => name.trim() && setStep("form")}
              disabled={!name.trim()}
              className="w-full mt-6 py-3 bg-[#4A5D4A] text-white rounded-full font-sans text-sm font-medium hover:bg-[#2D3A2D] transition-colors disabled:opacity-50"
              data-testid="start-contributing-btn"
            >
              Start Contributing
            </button>
          </motion.div>
        )}

        {/* Form step */}
        {step === "form" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Prompts */}
            {event.prompts.map((prompt, i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl p-6 shadow-sm" data-testid={`prompt-card-${i}`}>
                <p className="font-sans text-base font-medium text-[#2D3A2D] mb-3">{prompt}</p>
                <textarea
                  value={answers[i] || ""}
                  onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                  placeholder="Share your thoughts..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E] resize-y min-h-[80px]"
                  data-testid={`prompt-answer-${i}`}
                />
              </div>
            ))}

            {/* Photo upload */}
            <div className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl p-6 shadow-sm">
              <p className="font-sans text-base font-medium text-[#2D3A2D] mb-3">Add photos (optional)</p>
              <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" multiple className="hidden" />

              {photos.length > 0 && (
                <div className="flex gap-3 flex-wrap mb-4">
                  {photos.map((url, i) => (
                    <div key={i} className="relative group">
                      <img src={url} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-white shadow-md" />
                      <button onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-[#D4C9B8] rounded-xl font-sans text-sm text-[#7A8A7A] hover:bg-[#F8F6F1] transition-colors disabled:opacity-50"
                data-testid="upload-photos-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Upload Photos"}
              </button>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-[#C67B5C] to-[#B66B4C] text-white rounded-2xl font-sans font-medium text-base flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-60 shadow-lg"
              data-testid="submit-contribution-btn"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-5 h-5" /> Submit My Memories</>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ContributorPortal;
